import { Fragment, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { connect } from 'react-redux';
import { notify } from './components/Toast';
import { apiEndpoint } from './constants/api';
import Checkbox from './components/form/Checkbox';
import TextInput from './components/form/TextInput';
import SelectTextInput from './components/form/SelectTextInput';
import TextArea from './components/form/TextArea';
import SelectInput from './components/form/SelectInput';
import ToggleDatePicker from './components/form/ToggleDatePicker';
import DatePicker from './components/form/DatePicker';
import SelectRange from './components/form/SelectRange';
import CompanyLogo from './components/form/SelectLogo';
import CustomSelect from './components/form/CustomSelect';
import Header from './components/Header';
import Aside from './components/Aside';
import ProdzFooter from './components/Footer';
import Nav from './components/Nav';
import JobTypeOptions from './constants/jobTypes';
import PaymentTypeOptions from './constants/paymentTypes';
import StatusOptions from './constants/status';
import RichTextEditor from './components/Editor';
import { logBrazeEvent } from './utils/brazeEvents';

const connector = connect(
  (state, ownProps) => ({
    ...state.user,
    employer: state.user.currentEmployer,
  }),
  {}
);
function ManageJob({ user, employer }) {
  const navigate = useNavigate();
  const [jobData, setJobData] = useState({});
  const [confidential, setConfidential] = useState(!!jobData?.confidential);
  const [jobTitle, setTitle] = useState(jobData?.title || '');
  const [description, setDescription] = useState(jobData?.description || '');
  const [summary, setSummary] = useState(jobData?.summary || '');
  const [closed, setClosed] = useState(!!jobData?.closed);
  const [applicationLink, setApplicationLink] = useState(
    jobData?.application_link || ''
  );
  const [exclusive, setExclusive] = useState(!!jobData?.exclusive);
  const [jobType, setJobType] = useState(jobData?.job_type || '');
  const [postedDate, setPostedDate] = useState(
    jobData?.posted_date || new Date()
  );
  const [status, setStatus] = useState(jobData?.status || 0);
  const [jobStatus, setJobStatus] = useState(jobData?.job_status || 0);
  const [durationType, setDurationType] = useState(
    jobData?.duration_type_max || 0
  );
  const [durationMetric, setDurationMetric] = useState(
    jobData?.duration_metric_max || 0
  );
  const [paymentType, setPaymentType] = useState(jobData?.payment_type || 0);
  const [paymentMin, setPaymentMin] = useState(jobData?.hourly_rate || 0);
  const [paymentMax, setPaymentMax] = useState(jobData?.hourly_rate_max || 0);
  const [hiringContactName, setHiringContactName] = useState(
    jobData?.hiring_contact_name || ''
  );
  const [hiringContactEmail, setHiringContactEmail] = useState(
    jobData?.hiring_contact_email || ''
  );
  const [startDate, setStartDate] = useState(
    jobData?.start_date ? new Date(jobData?.start_date) : new Date()
  );
  const [endDate, setEndDate] = useState(
    jobData?.end_date ? new Date(jobData?.end_date) : new Date()
  );
  const [experienceLevel, setExperienceLevel] = useState(
    parseInt(jobData?.experience_level) || 0
  );
  const [jobTag, setJobTag] = useState(jobData?.job_tag || '');
  const [companyLogos, setCompanyLogos] = useState([]);
  const [logoId, setLogoId] = useState(jobData?.logo_id || null);
  const [companyName, setCompanyName] = useState(jobData?.company_name || null);
  const [city, setCity] = useState(jobData?.city || '');
  const [state, setState] = useState(jobData?.state || '');
  const [categories, setCategories] = useState([]);
  const [jobCategories, setJobCategories] = useState([]);
  const [remote, setRemote] = useState(!!jobData?.remote);
  const { slug } = useParams();

  const loadJobData = async (q) => {
    if (slug !== null && slug !== undefined) {
      const { data } = await axios.post(apiEndpoint(`/admin/job/${slug}`));
      setJobData(data[0]);
      loadJobCategories(data[0]);
    }
  };

  const loadCategories = async () => {
    const { data } = await axios.get(apiEndpoint(`/admin/categories`));
    setCategories(data);
  };

  const loadJobCategories = async ({ id }) => {
    const { data } = await axios.get(apiEndpoint(`/admin/categories/${id}`));
    setJobCategories(
      data.map(({ category_id, category_name }) => ({
        value: category_id,
        label: category_name,
      }))
    );
  };

  const loadCompanyLogos = async () => {
    const { data } = await axios.get(apiEndpoint(`/admin/job-posting/logos`));
    const logos = data
      .filter((l) => !!l.image)
      .map(({ id, company_name, image }) => ({
        id,
        label: company_name,
        company_name,
        image,
      }));

    setCompanyLogos(logos);
  };

  const durationOptions = [
    { label: 'Day(s)', value: 0, hideInput: false },
    { label: 'Week(s)', value: 1, hideInput: false },
    { label: 'Month(s)', value: 2, hideInput: false },
    { label: 'Fulltime', value: 3, hideInput: true },
    { label: 'Unknown', value: 4, hideInput: true },
  ];

  const jobTagOptions = [
    { value: 'music_or_radio', label: 'Music or Radio' },
    { value: 'film', label: 'Film' },
    { value: 'music', label: 'Music' },
    { value: 'tv', label: 'TV' },
    { value: 'broadcast', label: 'Broadcast' },
    { value: 'live_events', label: 'Live Events' },
    { value: 'miscellaneous', label: 'Miscellaneous' },
    { value: 'commercial', label: 'Commercial' },
  ];

  const postJob = async (evt) => {
    evt.preventDefault();
    const job = {
      title: jobTitle,
      description,
      summary,
      confidential,
      duration_type_max: durationType,
      duration_metric_max: durationMetric,
      closed,
      application_link: applicationLink,
      posted_date: postedDate,
      job_status: jobStatus,
      job_type: jobType && jobType.length ? jobType[0] || jobType : null,
      exclusive,
      status,
      user_id: user.id,
      payment_type: paymentType,
      hourly_rate: parseInt(paymentMin) || 0,
      hourly_rate_max: parseInt(paymentMax) || 0,
      employer: employer.value,
      hiring_contact_name: hiringContactName,
      hiring_contact_email: hiringContactEmail,
      start_date: startDate,
      end_date: endDate,
      experience_level: experienceLevel,
      job_tag: jobTag ? jobTag[0] : null,
      logo_id: logoId,
      company_name: companyName,
      city,
      state,
      remote,
      job_categories: (jobCategories || []).map(({ value, label }) => ({
        category_id: value,
      })),
    };
    if (!slug) {
      const { data } = await axios.post(apiEndpoint('/admin/job-posting'), job);

      logBrazeEvent('uploaded_job', {
        jobId: data.jobData.job_id,
        jobTitle: jobTitle,
        employerId: employer.value,
        timestamp: new Date().toISOString(),
      });

      notify('Job Posting successfully created!');
      navigate(`/admin/job-posting/${data.slug}`);
    } else {
      await axios.put(apiEndpoint(`/admin/job-posting/${slug}`), job);
      notify('Job Posting successfully updated!');
    }
  };

  const updateCompanyLogo = ({ id, company_name }) => {
    setLogoId(id);
    setCompanyName(company_name);
  };

  useEffect(() => {
    loadJobData();
    loadCompanyLogos();
    loadCategories();
  }, []);

  useEffect(() => {
    if (jobData) {
      setConfidential(!!jobData.confidential);
      setTitle(jobData.title || '');
      setDescription(jobData.description || '');
      setSummary(jobData.summary || '');
      setClosed(!!jobData.closed);
      setApplicationLink(jobData.application_link || '');
      setCompanyName(jobData.company_name || '');
      setExclusive(!!jobData.exclusive);
      setJobType(jobData.job_type || '');
      setPostedDate(jobData.posted_date || new Date());
      setStatus(jobData.status || 0);
      setJobStatus(jobData.job_status || 0);
      setDurationType(jobData.duration_type_max || 0);
      setDurationMetric(jobData.duration_metric_max || 0);
      setPaymentType(jobData.payment_type || 0);
      setPaymentMin(jobData.hourly_rate || 0);
      setPaymentMax(jobData.hourly_rate_max || 0);
      setHiringContactName(jobData.hiring_contact_name || '');
      setHiringContactEmail(jobData.hiring_contact_email || '');
      setStartDate(
        jobData.start_date ? new Date(jobData.start_date) : new Date()
      );
      setEndDate(jobData.end_date ? new Date(jobData.end_date) : new Date());
      setExperienceLevel(parseInt(jobData.experience_level) || 0);
      setJobTag(jobData.job_tag || '');
      setLogoId(jobData.logo_id || null);
      setCompanyName(jobData.company_name || null);
      setCity(jobData?.city || '');
      setState(jobData?.state || '');
      setRemote(!!jobData?.remote);
    }
  }, [jobData, slug]);

  return (
    <>
      <Helmet>
        <title>
          {`${jobTitle || 'Create New Job'}`} | Job | Productions.com
        </title>
      </Helmet>
      <div className="min-h-full">
        <Nav />
        <>
          <Header publicId={employer.logo} />
          <Aside />
          <main className="p-4 sm:ml-64">
            <div className="mx-auto sm:px-6 lg:px-8">
              {/* Replace with your content */}
              <form>
                <div className="w-full mb-2 bg-white border border-gray-200 rounded-lg shadow sm:p-8 dark:bg-gray-800 dark:border-gray-700">
                  <div className="space-y-6 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-6">
                    {/* Job Status */}
                    <SelectInput
                      label="Job Status"
                      description="Select your job status."
                      options={StatusOptions}
                      value={status}
                      onChange={(evt) => setStatus(Number(evt[0]))}
                    />

                    {/* Post */}
                    {status === 1 || status === 2 ? (
                      <div className="col-span-2">
                        <ToggleDatePicker
                          setSelectedDate={postedDate}
                          selectedDate={setPostedDate}
                        />
                      </div>
                    ) : null}
                    {/* Job Title */}
                    <div className="col-span-2">
                      <TextInput
                        label="Job Title"
                        description="Title of the Job"
                        value={jobTitle}
                        onChange={({ target: { value } }) => setTitle(value)}
                      />
                    </div>

                    {/* Job Application Link */}
                    <div className="col-span-2">
                      <TextInput
                        label="Job Application Link"
                        description="Job Application Link"
                        value={applicationLink}
                        onChange={({ target: { value } }) =>
                          setApplicationLink(value)
                        }
                      />
                    </div>

                    <div className="col-span-2">
                      <CompanyLogo
                        logos={companyLogos}
                        value={{ id: logoId }}
                        onChange={updateCompanyLogo}
                      />
                    </div>

                    {/* Job Description */}
                    <div className="col-span-2">
                      <RichTextEditor
                        label="Job Description"
                        description="Description of the Job"
                        value={description}
                        onChange={(value) => setDescription(value)}
                      />
                    </div>

                    {/* Job Summary */}
                    <div className="col-span-2">
                      <TextArea
                        label="Job Summary"
                        description="Summary of the Job"
                        value={summary}
                        onChange={({ target: { value } }) => setSummary(value)}
                      />
                    </div>

                    {/* Start Date */}
                    <div className="col-span-1">
                      <DatePicker
                        label="Job Submission Start Date"
                        description="Start Date Accepting Candidates"
                        selectedDate={startDate}
                        onChange={(value) => setStartDate(value)}
                      />
                    </div>

                    {/* End Date */}
                    <div className="col-span-1">
                      <DatePicker
                        label="Job Submission End Date"
                        description="End Date Accepting Candidates"
                        selectedDate={endDate}
                        onChange={(value) => setEndDate(value)}
                      />
                    </div>

                    {/* Job Duration */}
                    <div className="col-span-1">
                      <SelectTextInput
                        label="Job Duration"
                        description="Duration of the Job"
                        options={durationOptions}
                        unit={durationType}
                        setUnit={(value) => setDurationType(parseInt(value))}
                        value={durationMetric}
                        setValue={(value) => setDurationMetric(parseInt(value))}
                      />
                    </div>

                    {/* Pay Structure */}
                    <div className="col-span-1">
                      <SelectRange
                        label="Pay Structure"
                        options={PaymentTypeOptions}
                        unit={paymentType}
                        setUnit={(value) => setPaymentType(value)}
                        minValue={paymentMin}
                        setMinValue={(value) => setPaymentMin(value)}
                        maxValue={paymentMax}
                        setMaxValue={(value) => setPaymentMax(value)}
                      />
                    </div>

                    <SelectInput
                      label="Job Type"
                      description="Select your job type."
                      options={JobTypeOptions}
                      value={jobType}
                      onChange={(value) => setJobType(value)}
                    />

                    {/* Experience Link */}
                    <div className="col-span-1">
                      <TextInput
                        label="Experience"
                        description="Years of Experience"
                        type="number"
                        value={experienceLevel}
                        onChange={({ target: { value } }) =>
                          setExperienceLevel(parseInt(value))
                        }
                      />
                    </div>

                    <div className="col-span-1">
                      <SelectInput
                        label="Job Tag"
                        description="Select your job tag."
                        options={jobTagOptions}
                        value={jobTag}
                        onChange={(value) => setJobTag(value)}
                      />
                    </div>

                    {/* Confidential and Remote Job Checkboxes */}
                    <div className="flex space-x-4">
                      <div className="flex flex-col">
                        <label className="text-gray-700 font-medium">
                          Is this job confidential?
                        </label>
                        <Checkbox
                          label="Confidential"
                          checked={confidential}
                          onChange={({ target: { checked } }) =>
                            setConfidential(checked)
                          }
                        />
                      </div>

                      <div className="flex flex-col">
                        <label className="text-gray-700 font-medium">
                          Is this job remote?
                        </label>
                        <Checkbox
                          label="Remote"
                          checked={remote}
                          onChange={({ target: { checked } }) =>
                            setRemote(checked)
                          }
                        />
                      </div>
                    </div>

                    {/* City */}
                    <div className="col-span-1">
                      <TextInput
                        label="Primary City"
                        description="City"
                        value={city}
                        onChange={({ target: { value } }) => setCity(value)}
                      />
                    </div>

                    {/* State */}
                    <div className="col-span-1">
                      <TextInput
                        label="Primary State"
                        description="State"
                        value={state}
                        onChange={({ target: { value } }) => setState(value)}
                      />
                    </div>

                    <div className="col-span-2">
                      <CustomSelect
                        label="Job Categories"
                        options={categories}
                        value={jobCategories}
                        onChange={(value) => setJobCategories(value)}
                        isMulti
                      />
                    </div>

                    {/* Hiring Contact Name */}
                    <div className="col-span-1">
                      <TextInput
                        label="Hiring Contact Name"
                        description="Contact Name"
                        value={hiringContactName}
                        onChange={({ target: { value } }) =>
                          setHiringContactName(value)
                        }
                      />
                    </div>

                    {/* Job Title */}
                    <div className="col-span-1">
                      <TextInput
                        label="Hiring Contact Email"
                        description="Contact Email"
                        value={hiringContactEmail}
                        onChange={({ target: { value } }) =>
                          setHiringContactEmail(value)
                        }
                      />
                    </div>
                  </div>
                  <div className="my-2">
                    <button
                      type="submit"
                      onClick={postJob}
                      className="text-white bg-blue-600 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-red-300 dark:focus:ring-red-800 font-medium rounded-lg text-lg inline-flex items-center px-5 py-2.5 text-center mr-2"
                    >
                      {jobData?.id ? 'Update' : 'Create'} Job Posting
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/admin/jobs')}
                      className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-lg font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
            {/* /End replace */}
            <ProdzFooter />
          </main>
        </>
      </div>
    </>
  );
}

export default connector(ManageJob);
